import { repaintPageShellBg } from "./pageShellBg";

/** layout 视口底 → 物理屏底的延伸高度（CSS 变量名） */
export const BLEED_BOTTOM_VAR = "--lf-h5-bleed-bottom";
/** 物理屏高度（CSS 变量名，= innerHeight + bleed） */
export const SCREEN_HEIGHT_VAR = "--lf-h5-screen-height";

export function measureSafeAreaTopPx(): number {
  const probe = document.createElement("div");
  probe.style.cssText =
    "position:fixed;visibility:hidden;pointer-events:none;padding-top:env(safe-area-inset-top)";
  document.documentElement.appendChild(probe);
  const px = parseFloat(getComputedStyle(probe).paddingTop) || 0;
  probe.remove();
  return Math.round(px);
}

function measureSafeAreaBottomPx(): number {
  const probe = document.createElement("div");
  probe.style.cssText =
    "position:fixed;visibility:hidden;pointer-events:none;padding-bottom:env(safe-area-inset-bottom)";
  document.documentElement.appendChild(probe);
  const px = parseFloat(getComputedStyle(probe).paddingBottom) || 0;
  probe.remove();
  return Math.round(px);
}

function measureScreenCssHeightPx(): number {
  const sw = window.screen.width;
  const sh = window.screen.height;
  const portrait = window.innerHeight >= window.innerWidth;
  return portrait ? Math.max(sh, sw) : Math.min(sh, sw);
}

function isIosLike(): boolean {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

/**
 * layout 底到物理屏底需向下延伸的高度。
 * env(safe-area-inset-bottom) 通常只有 Home 条（如 34px），
 * screen.height - innerHeight 才是完整差值（如 62px）。
 */
export function measureBleedBottomPx(): number {
  const fromEnv = measureSafeAreaBottomPx();
  const inner = window.innerHeight;
  const fromScreen = Math.max(0, measureScreenCssHeightPx() - inner);
  if (isIosLike() && fromScreen > 0) {
    return Math.max(fromEnv, fromScreen);
  }
  return fromEnv;
}

function cssVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || "(empty)";
}

function rectLine(label: string, el: Element | null): string[] {
  if (!el) {
    return [`${label}: (not found)`];
  }
  const r = el.getBoundingClientRect();
  const cs = getComputedStyle(el);
  return [
    `${label}.top: ${Math.round(r.top)}`,
    `${label}.bottom: ${Math.round(r.bottom)}`,
    `${label}.height: ${Math.round(r.height)}`,
    `${label}.position: ${cs.position}`,
    `${label}.paddingBottom: ${cs.paddingBottom}`,
    `${label}.gapToInnerBottom: ${Math.round(window.innerHeight - r.bottom)}`,
  ];
}

/** 真机视口 / Tab 诊断报告（供调试弹框复制） */
export function collectViewportDebugReport(): string {
  const vv = window.visualViewport;
  const tabShell = document.querySelector(".space-tab-bar-shell");
  const spaceShell = document.querySelector(".lf-h5-space-shell");
  const html = document.documentElement;
  const root = document.getElementById("root");
  const bleedPx = measureBleedBottomPx();
  const safeBottomPx = measureSafeAreaBottomPx();
  const screenCssH = measureScreenCssHeightPx();
  const lines = [
    `time: ${new Date().toISOString()}`,
    `url: ${window.location.href}`,
    `ua: ${navigator.userAgent}`,
    `iosLike: ${isIosLike()}`,
    `devicePixelRatio: ${window.devicePixelRatio}`,
    `innerWidth: ${window.innerWidth}`,
    `innerHeight: ${window.innerHeight}`,
    `outerWidth: ${window.outerWidth}`,
    `outerHeight: ${window.outerHeight}`,
    `screen.width: ${window.screen.width}`,
    `screen.height: ${window.screen.height}`,
    `screenCssHeight(portrait logic): ${screenCssH}`,
    `screenMinusInner: ${Math.max(0, screenCssH - window.innerHeight)}`,
    `measureSafeAreaTopPx: ${measureSafeAreaTopPx()}`,
    `measureSafeAreaBottomPx: ${safeBottomPx}`,
    `measureBleedBottomPx: ${bleedPx}`,
    `css --lf-h5-bleed-bottom: ${cssVar(BLEED_BOTTOM_VAR)}`,
    `css --lf-h5-screen-height: ${cssVar(SCREEN_HEIGHT_VAR)}`,
    `css --lf-h5-tab-bar-height: ${cssVar("--lf-h5-tab-bar-height")}`,
    `css --lf-h5-tab-bar-content-height: ${cssVar("--lf-h5-tab-bar-content-height")}`,
    `html.clientHeight: ${html.clientHeight}`,
    `html.offsetHeight: ${html.offsetHeight}`,
    `html.style.height: ${html.style.height || "(inline empty)"}`,
    `html.computed.height: ${getComputedStyle(html).height}`,
    `body.clientHeight: ${document.body.clientHeight}`,
    `#root.clientHeight: ${root?.clientHeight ?? "(missing)"}`,
    ...rectLine("spaceShell", spaceShell),
    ...rectLine("tabShell", tabShell),
  ];

  if (vv) {
    lines.push(
      `visualViewport.width: ${Math.round(vv.width)}`,
      `visualViewport.height: ${Math.round(vv.height)}`,
      `visualViewport.offsetTop: ${Math.round(vv.offsetTop)}`,
      `visualViewport.offsetLeft: ${Math.round(vv.offsetLeft)}`,
      `visualViewport.scale: ${vv.scale}`
    );
  } else {
    lines.push("visualViewport: (unsupported)");
  }

  return lines.join("\n");
}

/** 写入物理屏高度到 html；iOS fixed 层绘制不跟 box 高度走，须用文档流 */
export function applyViewportFill(): void {
  const bleed = measureBleedBottomPx();
  const screenHeight = window.innerHeight + bleed;
  const doc = document.documentElement;
  const px = `${screenHeight}px`;
  doc.style.setProperty(BLEED_BOTTOM_VAR, `${bleed}px`);
  doc.style.setProperty(SCREEN_HEIGHT_VAR, px);
  doc.style.height = px;
  const body = document.body;
  if (body) {
    body.style.height = px;
    body.style.minHeight = px;
  }
  repaintPageShellBg();
}

if (typeof window !== "undefined") {
  window.__lfH5ApplyViewport = applyViewportFill;
}

export function initViewportFill(): () => void {
  applyViewportFill();
  const refresh = () => applyViewportFill();
  window.addEventListener("resize", refresh);
  window.addEventListener("orientationchange", refresh);
  window.visualViewport?.addEventListener("resize", refresh);
  return () => {
    window.removeEventListener("resize", refresh);
    window.removeEventListener("orientationchange", refresh);
    window.visualViewport?.removeEventListener("resize", refresh);
  };
}
